/**
 * ChatInputArea — text input, file upload, attachment preview, send/stop
 */

import { useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { FileText, Paperclip, Send, Square, X } from 'lucide-react';
import type { Attachment } from '../../../features/chat/api';
import styles from '../AiConsultantPage.module.css';

interface ChatInputAreaProps {
  inputValue: string;
  onInputChange: (value: string) => void;
  selectedAttachments: Attachment[];
  isUploading: boolean;
  isTyping: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  onFilesSelected: (files: FileList | File[]) => void;
  onRemoveAttachment: (index: number) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

const CHAT_ATTACHMENT_ACCEPT = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.pdf',
].join(',');

export function ChatInputArea({
  inputValue,
  onInputChange,
  selectedAttachments,
  isUploading,
  isTyping,
  onSend,
  onStop,
  onFilesSelected,
  onRemoveAttachment,
  onKeyDown,
}: ChatInputAreaProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const canAttachMore = selectedAttachments.length < 5 && !isUploading;

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (e.clipboardData.files.length === 0) return;
    e.preventDefault();
    onFilesSelected(e.clipboardData.files);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length === 0) return;
    onFilesSelected(e.dataTransfer.files);
  };

  return (
    <div
      className={`${styles.inputArea} ${isDragging ? styles.inputAreaDragging : ''}`}
      onDragEnter={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        setIsDragging(false);
      }}
      onDrop={handleDrop}
    >
      {selectedAttachments.length > 0 && (
        <div className={styles.attachmentPreviewList}>
          {selectedAttachments.map((att, idx) => (
            <div key={`${att.url}-${idx}`} className={styles.attachmentPreviewItem}>
              {att.mimeType.startsWith('image/') ? (
                <img
                  src={att.url.startsWith('oss://') ? '#' : att.url}
                  alt={att.fileName}
                  className={styles.attachmentThumb}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div className={styles.attachmentFileIcon}>
                  <FileText className={styles.attachmentPreviewIcon} />
                  <span className={styles.attachmentFileName}>{att.fileName}</span>
                </div>
              )}
              <button
                className={styles.attachmentRemoveBtn}
                onClick={() => onRemoveAttachment(idx)}
                type="button"
                aria-label={`移除附件 ${att.fileName}`}
              >
                <X className={styles.attachmentRemoveIcon} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className={styles.inputRow}>
        <input
          ref={fileInputRef}
          type="file"
          accept={CHAT_ATTACHMENT_ACCEPT}
          multiple
          className={styles.fileInput}
          onChange={(e) => {
            if (e.currentTarget.files) onFilesSelected(e.currentTarget.files);
            e.currentTarget.value = '';
          }}
        />
        <button
          type="button"
          className={styles.uploadButton}
          onClick={() => fileInputRef.current?.click()}
          disabled={!canAttachMore}
          aria-label="添加图片或 PDF 附件"
          title="添加图片或 PDF 附件"
        >
          {isUploading ? <span className={styles.uploadSpinner} /> : <Paperclip className={styles.inputIcon} />}
        </button>
        <TextareaAutosize
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={handlePaste}
          placeholder={selectedAttachments.length > 0 ? '输入问题（可选）...' : '输入你的问题...'}
          className={styles.textInput}
          minRows={1}
          maxRows={5}
        />
        {isTyping ? (
          <button onClick={onStop} className={styles.sendButton} type="button" aria-label="停止生成">
            <Square className={styles.inputIcon} fill="currentColor" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onSend(inputValue)}
            disabled={!inputValue.trim() && selectedAttachments.length === 0}
            className={styles.sendButton}
            aria-label="发送"
          >
            <Send className={styles.inputIcon} />
          </button>
        )}
      </div>
    </div>
  );
}
